import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Code,
  Container,
  Flex,
  Heading,
  Select,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useAuth } from "react-oidc-context";
import {
  getRecentWorks,
  getWorkPayloads,
  makeApiClient,
  type PayloadEntry,
  type RecentWork,
} from "../api/client";

const POLL_INTERVAL_MS = 2500;

export default function MonitorPage() {
  const auth = useAuth();
  const client = useMemo(
    () => makeApiClient(auth.user?.id_token),
    [auth.user?.id_token],
  );

  const [works, setWorks] = useState<RecentWork[]>([]);
  const [selectedWorkId, setSelectedWorkId] = useState<string>("");
  const [payloads, setPayloads] = useState<PayloadEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const sinceRef = useRef<string | undefined>(undefined);

  // Load the dropdown on mount and whenever auth swaps tokens.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const recent = await getRecentWorks(client);
        if (cancelled) return;
        setWorks(recent);
        if (recent.length > 0 && !selectedWorkId) {
          setSelectedWorkId(recent[0].work_id);
        }
      } catch (e) {
        if (!cancelled) setError(asMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Reset payloads + cursor whenever the selection changes, then poll.
  useEffect(() => {
    if (!selectedWorkId) return;
    sinceRef.current = undefined;
    // Resetting on selection change is the `key`-remount pattern written as an
    // effect. Fixing it properly means lifting the poll into a child keyed by
    // selectedWorkId, which changes this component's render contract — out of
    // scope for enabling lint. Same class as the customer-app bucket-A
    // suppressions tracked in 86bb7d1zk.
    /* eslint-disable react-hooks/set-state-in-effect */
    setPayloads([]);
    setError(null);
    /* eslint-enable react-hooks/set-state-in-effect */

    let cancelled = false;

    async function tick() {
      try {
        const resp = await getWorkPayloads(client, selectedWorkId, sinceRef.current);
        if (cancelled) return;
        if (resp.payloads.length > 0) {
          sinceRef.current = resp.next_since;
          setPayloads((prev) => [...prev, ...resp.payloads]);
        }
      } catch (e) {
        if (!cancelled) setError(asMessage(e));
      }
    }

    void tick();
    const handle = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [selectedWorkId, client]);

  return (
    <Container maxW="container.xl" py={6}>
      <VStack align="stretch" spacing={4}>
        <Flex align="center" justify="space-between">
          <Heading size="md">Dawson's Creek — DAW Monitor</Heading>
          <Text fontSize="sm" color="gray.500">{auth.user?.profile.email ?? ""}</Text>
        </Flex>

        <Select
          value={selectedWorkId}
          onChange={(e) => setSelectedWorkId(e.target.value)}
          placeholder={works.length === 0 ? "Loading recent works…" : undefined}
        >
          {works.map((w) => (
            <option key={w.work_id} value={w.work_id}>
              {w.creator || "(no pseudonym)"} — {w.title || w.work_id.slice(0, 8)}
              {w.created_at ? ` — ${w.created_at}` : ""}
            </option>
          ))}
        </Select>

        {error && (
          <Box bg="red.50" color="red.700" p={3} borderRadius="md">
            <Text fontWeight="bold">Error</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
        )}

        {selectedWorkId && payloads.length === 0 && !error && (
          <Flex align="center" color="gray.500"><Spinner size="sm" mr={2} /> Waiting for payloads…</Flex>
        )}

        <VStack align="stretch" spacing={2}>
          {payloads.map((p) => (
            <Box key={p.evidence_id} bg="gray.50" p={3} borderRadius="md" borderWidth="1px">
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" color="gray.500">{p.received_at ?? "—"}</Text>
                <Text fontSize="xs" color="gray.400" fontFamily="mono">{p.evidence_id.slice(0, 8)}</Text>
              </Flex>
              <Code fontSize="xs" whiteSpace="pre-wrap" w="full" display="block" p={2}>
                {JSON.stringify(p.supporting_data, null, 2)}
              </Code>
            </Box>
          ))}
        </VStack>
      </VStack>
    </Container>
  );
}

function asMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) return String((e as { message: unknown }).message);
  return String(e);
}
